import React, { useMemo, useState } from "react";
import {
  Button,
  Group,
  Text,
  Title,
  useMantineColorScheme,
  Container,
  Paper,
  Select,
  MultiSelect,
  Loader,
  Center,
  Alert,
  Divider,
  Avatar,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft, IconSend, IconAlertCircle, IconBuilding } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import {
  useMeQuery,
  useAllActiveFranchisesWithStaffQuery,
  useUserSearchQuery,
  useCreateTransactionMutation,
  useFranchiseTeamsQuery,
  TransactionType,
} from "generated/graphql";
import { getTransactionTypeLabel, getTransactionTypeColor } from "../TransactionManagerHub/transactionUtils";

/**
 * Hook to get franchises managed by the current user
 */
function useUserFranchises() {
  const { data: meData, loading: meLoading, error: meError } = useMeQuery();
  const {
    data: franchisesData,
    loading: franchisesLoading,
    error: franchisesError,
  } = useAllActiveFranchisesWithStaffQuery();

  const userId = meData?.me?.userData?.id;

  const managedFranchises = useMemo(() => {
    const items = franchisesData?.franchises?.items;
    if (!userId || !items || !Array.isArray(items)) return [];

    return items
      .filter((f) => f.GM?.id === userId || f.AGM?.id === userId)
      .map((f) => ({
        id: f.id,
        name: f.name,
        prefix: f.prefix ?? null,
        logoUrl: f.logoUrl ?? null,
        role: f.GM?.id === userId ? ("GM" as const) : ("AGM" as const),
      }));
  }, [userId, franchisesData]);

  const allFranchises = useMemo(() => {
    const items = franchisesData?.franchises?.items;
    if (!items || !Array.isArray(items)) return [];
    return items.map((f) => ({
      id: f.id,
      name: f.name,
      prefix: f.prefix ?? null,
      logoUrl: f.logoUrl ?? null,
    }));
  }, [franchisesData]);

  return {
    userId,
    franchises: managedFranchises,
    allFranchises,
    loading: meLoading || franchisesLoading,
    error: meError || franchisesError,
  };
}

/**
 * Transaction types that require a trade partner
 */
const TRADE_TYPES = [TransactionType.Trade];

/**
 * Transaction types for roster moves (sign, cut, etc.)
 */
const ROSTER_TYPES = [
  TransactionType.Sign,
  TransactionType.Cut,
  TransactionType.Captain,
  TransactionType.Ir,
  TransactionType.Promote,
  TransactionType.Relegate,
  TransactionType.Resign,
  TransactionType.Sub,
];

/**
 * All available transaction types for selection
 * Note: Removed group property to avoid potential Mantine Select grouping issues
 */
const TRANSACTION_TYPE_OPTIONS = [
  { value: TransactionType.Sign, label: getTransactionTypeLabel(TransactionType.Sign) },
  { value: TransactionType.Cut, label: getTransactionTypeLabel(TransactionType.Cut) },
  { value: TransactionType.Trade, label: getTransactionTypeLabel(TransactionType.Trade) },
  { value: TransactionType.Captain, label: getTransactionTypeLabel(TransactionType.Captain) },
  { value: TransactionType.Ir, label: getTransactionTypeLabel(TransactionType.Ir) },
  { value: TransactionType.Promote, label: getTransactionTypeLabel(TransactionType.Promote) },
  { value: TransactionType.Relegate, label: getTransactionTypeLabel(TransactionType.Relegate) },
  { value: TransactionType.Resign, label: getTransactionTypeLabel(TransactionType.Resign) },
  { value: TransactionType.Sub, label: getTransactionTypeLabel(TransactionType.Sub) },
];

/**
 * TransactionCreate
 *
 * Form for GMs to create new transactions.
 */
export default function TransactionCreate() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const { userId, franchises: rawFranchises, allFranchises: rawAllFranchises, loading: userLoading, error: userError } = useUserFranchises();

  // Ensure franchises is always an array (defensive programming)
  const franchises = Array.isArray(rawFranchises) ? rawFranchises : [];
  const allFranchises = Array.isArray(rawAllFranchises) ? rawAllFranchises : [];

  // Form state
  const [selectedFranchiseId, setSelectedFranchiseId] = useState<string | null>(null);
  const [transactionType, setTransactionType] = useState<TransactionType | null>(null);
  const [tradePartnerFranchiseId, setTradePartnerFranchiseId] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [selectedPlayersMap, setSelectedPlayersMap] = useState<Map<string, string>>(new Map());
  const [playerSearch, setPlayerSearch] = useState("");

  const activeFranchiseId = selectedFranchiseId || franchises[0]?.id;
  const activeFranchise = franchises.find((f) => f.id === activeFranchiseId);

  // Query teams for the selected franchise
  const { data: teamsData, loading: teamsLoading } = useFranchiseTeamsQuery({
    variables: { franchiseId: activeFranchiseId ?? "" },
    skip: !activeFranchiseId,
  });

  // Team options for the selected franchise
  const teamOptions = useMemo(() => {
    const items = teamsData?.teams?.items;
    if (!items || !Array.isArray(items)) return [];
    return items
      .filter((t) => t.active)
      .map((t) => ({
        value: t.id,
        label: `${t.name}${t.tier ? ` (${t.tier})` : ""}`,
      }));
  }, [teamsData]);

  // Player search query
  const { data: playerSearchData, loading: playerSearchLoading } = useUserSearchQuery({
    variables: { input: playerSearch },
    skip: playerSearch.length < 2,
  });

  // Build player options combining search results with already-selected players
  const playerOptions = useMemo(() => {
    const items = playerSearchData?.users?.items;
    const searchResults = items && Array.isArray(items)
      ? items.map((u) => ({ value: u.id, label: u.name }))
      : [];

    // Add already-selected players that aren't in search results
    const searchIds = new Set(searchResults.map((r) => r.value));
    const selectedNotInSearch = selectedPlayerIds
      .filter((id) => !searchIds.has(id) && selectedPlayersMap.has(id))
      .map((id) => ({ value: id, label: selectedPlayersMap.get(id) || id }));

    return [...selectedNotInSearch, ...searchResults];
  }, [playerSearchData, selectedPlayerIds, selectedPlayersMap]);

  // Handler to update both selectedPlayerIds and the name map
  function handlePlayerSelection(ids: string[]) {
    // Add newly selected players to the map
    const newMap = new Map(selectedPlayersMap);
    for (const opt of playerOptions) {
      if (ids.includes(opt.value)) {
        newMap.set(opt.value, opt.label);
      }
    }
    // Remove deselected players
    for (const id of selectedPlayersMap.keys()) {
      if (!ids.includes(id)) {
        newMap.delete(id);
      }
    }
    setSelectedPlayersMap(newMap);
    setSelectedPlayerIds(ids);
  }

  // Trade partner options (exclude current franchise)
  const tradePartnerOptions = useMemo(() => {
    if (!allFranchises || !Array.isArray(allFranchises)) return [];
    return allFranchises
      .filter((f) => f.id !== activeFranchiseId)
      .map((f) => ({
        value: f.id,
        label: f.prefix ? `[${f.prefix}] ${f.name}` : f.name,
      }));
  }, [allFranchises, activeFranchiseId]);

  const [createTransaction, { loading: createLoading }] = useCreateTransactionMutation();

  const isTradeType = transactionType !== null && TRADE_TYPES.includes(transactionType);

  // Transaction types that require team selection
  const requiresTeamSelection = transactionType !== null && ROSTER_TYPES.includes(transactionType);

  async function handleSubmit() {
    console.log('[TransactionCreate] handleSubmit called', { userId, activeFranchiseId, transactionType, selectedTeamId, selectedPlayerIds });

    if (!userId || !activeFranchiseId || !transactionType || selectedPlayerIds.length === 0) {
      console.log('[TransactionCreate] Validation failed', { userId, activeFranchiseId, transactionType, selectedPlayerIds: selectedPlayerIds.length });
      notifications.show({
        title: "Validation Error",
        message: `Please fill in all required fields. Missing: ${!userId ? 'userId ' : ''}${!activeFranchiseId ? 'franchise ' : ''}${!transactionType ? 'type ' : ''}${selectedPlayerIds.length === 0 ? 'players' : ''}`,
        color: "red",
      });
      return;
    }

    if (isTradeType && !tradePartnerFranchiseId) {
      notifications.show({
        title: "Validation Error",
        message: "Please select a trade partner franchise.",
        color: "red",
      });
      return;
    }

    // Require team selection for roster transactions (Sign, Sub, etc.)
    if (requiresTeamSelection && !selectedTeamId) {
      notifications.show({
        title: "Validation Error",
        message: "Please select a team for this transaction.",
        color: "red",
      });
      return;
    }

    try {
      console.log('[TransactionCreate] Calling createTransaction mutation...');
      const result = await createTransaction({
        variables: {
          input: {
            submitterId: userId,
            submittingFranchiseId: activeFranchiseId,
            tradePartnerFranchiseId: isTradeType ? tradePartnerFranchiseId : undefined,
            transactionType,
            transactionItems: selectedPlayerIds.map((playerId) => ({
              playerId,
              // itemReceiverId is the team receiving the player (for Sign, Sub, Trade)
              itemReceiverId: selectedTeamId || undefined,
            })),
          },
        },
      });

      console.log('[TransactionCreate] Mutation result:', result);

      if (result.data?.createTransaction) {
        notifications.show({
          title: "Transaction Created",
          message: `Your ${getTransactionTypeLabel(transactionType)} transaction has been submitted for approval.`,
          color: "green",
        });
        navigate("/gm-hub");
      } else if (result.errors) {
        console.error('[TransactionCreate] GraphQL errors:', result.errors);
        notifications.show({
          title: "Error",
          message: `GraphQL error: ${result.errors.map(e => e.message).join(', ')}`,
          color: "red",
        });
      }
    } catch (err: unknown) {
      console.error('[TransactionCreate] Mutation exception:', err);
      notifications.show({
        title: "Error",
        message: `Failed to create transaction. ${err instanceof Error ? err.message : "Unknown error"}`,
        color: "red",
      });
    }
  }

  function handleReset() {
    setTransactionType(null);
    setTradePartnerFranchiseId(null);
    setSelectedTeamId(null);
    setSelectedPlayerIds([]);
    setSelectedPlayersMap(new Map());
    setPlayerSearch("");
  }

  if (userLoading) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (userError) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load your franchise information. {userError.message}
        </Alert>
      </Container>
    );
  }

  if (franchises.length === 0) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Alert icon={<IconAlertCircle size={16} />} title="No Franchise Assigned" color="yellow">
          You are not currently assigned as GM or AGM of any franchise.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/gm-hub")}
          size="sm"
        >
          Back to GM Hub
        </Button>
      </Group>

      <div className="space-y-4">
        <div>
          <Title c={isDark ? "white" : "black"} size="h3" className="sm:text-2xl">
            Create Transaction
          </Title>
          <Text c={isDark ? "gray.4" : "gray.7"} size="sm">
            Submit a new roster move or trade for approval.
          </Text>
        </div>

        <Paper withBorder radius="md" shadow="sm" p="lg">
          <div className="space-y-6">
            {/* Franchise Selection (if multiple) */}
            {franchises.length > 1 && (
              <Select
                label="Submitting Franchise"
                description="Select which franchise is submitting this transaction"
                data={franchises.map((f) => ({
                  value: f.id,
                  label: f.prefix ? `[${f.prefix}] ${f.name}` : f.name,
                }))}
                value={activeFranchiseId}
                onChange={(value) => {
                  setSelectedFranchiseId(value);
                  handleReset();
                }}
                leftSection={
                  activeFranchise?.logoUrl ? (
                    <Avatar src={activeFranchise.logoUrl} size="xs" radius="sm" />
                  ) : (
                    <IconBuilding size={14} />
                  )
                }
              />
            )}

            {/* Current Franchise Display (if single) */}
            {franchises.length === 1 && activeFranchise && (
              <div>
                <Text size="sm" fw={500} mb={4}>
                  Submitting Franchise
                </Text>
                <Group gap="xs">
                  <Avatar src={activeFranchise.logoUrl} size="sm" radius="sm">
                    <IconBuilding size={14} />
                  </Avatar>
                  <Text>
                    {activeFranchise.prefix ? `[${activeFranchise.prefix}] ` : ""}
                    {activeFranchise.name}
                  </Text>
                </Group>
              </div>
            )}

            <Divider />

            {/* Transaction Type */}
            <Select
              label="Transaction Type"
              description="What type of roster move are you making?"
              placeholder="Select transaction type"
              data={TRANSACTION_TYPE_OPTIONS}
              value={transactionType}
              onChange={(value) => {
                setTransactionType(value as TransactionType);
                setTradePartnerFranchiseId(null);
                setSelectedPlayerIds([]);
              }}
              required
            />

            {/* Trade Partner (for trades) */}
            {isTradeType && (
              <Select
                label="Trade Partner"
                description="Select the franchise you are trading with"
                placeholder="Select franchise"
                data={tradePartnerOptions}
                value={tradePartnerFranchiseId}
                onChange={setTradePartnerFranchiseId}
                searchable
                required
              />
            )}

            {/* Team Selection (for roster transactions) */}
            {requiresTeamSelection && (
              <Select
                label="Team"
                description="Select the team for this roster move"
                placeholder={teamsLoading ? "Loading teams..." : "Select team"}
                data={teamOptions}
                value={selectedTeamId}
                onChange={setSelectedTeamId}
                disabled={teamsLoading || teamOptions.length === 0}
                required
                nothingFoundMessage={
                  teamsLoading
                    ? "Loading..."
                    : "No active teams found for this franchise"
                }
              />
            )}

            {/* Player Selection */}
            {transactionType && (
              <MultiSelect
                label="Players"
                description={
                  isTradeType
                    ? "Select players involved in this trade"
                    : "Select players for this transaction"
                }
                placeholder="Search for players..."
                data={playerOptions}
                value={selectedPlayerIds}
                onChange={handlePlayerSelection}
                searchable
                searchValue={playerSearch}
                onSearchChange={setPlayerSearch}
                nothingFoundMessage={
                  playerSearch.length < 2
                    ? "Type at least 2 characters to search"
                    : playerSearchLoading
                      ? "Searching..."
                      : "No players found"
                }
                required
                comboboxProps={{ zIndex: 10000 }}
              />
            )}

            {/* Summary */}
            {transactionType && selectedPlayerIds.length > 0 && (
              <>
                <Divider />
                <div>
                  <Text size="sm" fw={500} mb="xs">
                    Transaction Summary
                  </Text>
                  <Paper withBorder p="md" radius="sm" bg={isDark ? "dark.6" : "gray.0"}>
                    <Text size="sm">
                      <strong>Type:</strong> {getTransactionTypeLabel(transactionType)}
                    </Text>
                    <Text size="sm">
                      <strong>Franchise:</strong>{" "}
                      {activeFranchise?.prefix
                        ? `[${activeFranchise.prefix}] ${activeFranchise.name}`
                        : activeFranchise?.name}
                    </Text>
                    {isTradeType && tradePartnerFranchiseId && (
                      <Text size="sm">
                        <strong>Trade Partner:</strong>{" "}
                        {tradePartnerOptions.find((f) => f.value === tradePartnerFranchiseId)?.label}
                      </Text>
                    )}
                    {requiresTeamSelection && selectedTeamId && (
                      <Text size="sm">
                        <strong>Team:</strong>{" "}
                        {teamOptions.find((t) => t.value === selectedTeamId)?.label}
                      </Text>
                    )}
                    <Text size="sm">
                      <strong>Players:</strong> {selectedPlayerIds.length} selected
                    </Text>
                  </Paper>
                </div>
              </>
            )}

            {/* Actions */}
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={handleReset}>
                Reset
              </Button>
              <Button
                color="green"
                leftSection={<IconSend size={16} />}
                onClick={handleSubmit}
                loading={createLoading}
                disabled={
                  !transactionType ||
                  selectedPlayerIds.length === 0 ||
                  (isTradeType && !tradePartnerFranchiseId) ||
                  (requiresTeamSelection && !selectedTeamId)
                }
              >
                Submit Transaction
              </Button>
            </Group>
          </div>
        </Paper>
      </div>
    </Container>
  );
}
